
import { Component, Mixins, Prop, Watch } from "vue-property-decorator"
import {$retrievalPeriod} from '@/scripts/common'
import Common from '../mixins/common';
import { endBefore, getCountFromServer, getDoc, onSnapshot, orderBy, query, QueryConstraint, startAt, Unsubscribe, where } from "firebase/firestore";
import { MessageBox, MessageFilter, MessageFilterValue, MessageProps, NoticeProps, UserReceivedMessage, UserSentMessage } from "@/scripts/datatypes";
import ModalConfirm from "@/components/ModalConfirm.vue"
import ModalAlert from "@/components/ModalAlert.vue"
import SchedulingObject from "@/components/SchedulingObject.vue"
import { DateTime } from "luxon";


@Component({
  components: {
    ModalAlert,
    ModalConfirm,
    SchedulingObject
  }
})

export default class UserInboxOnReferenceObjects extends Mixins(Common) {
  @Prop({type: Array, default: () => {return []}}) private referenceObjectIds?: string[]

  isLoading: (boolean | string) = false

  observers: (Unsubscribe | undefined)[] = []
  receivedMessages: UserReceivedMessage[] = []
  sentReceivedMessages: UserReceivedMessage[] = []
  messageProps: MessageProps = {}
  sentMessages: UserSentMessage[] = []

  messageFilters: Array<MessageFilter> = [
    {value: 'all', name: 'messenger.all',count: 0},
    {value: 'unread', name: 'messenger.unread',count: 0},
    {value: 'pending', name: 'messenger.pending',count: 0},
    {value: 'draft', name: 'messenger.draft',count: 0}
  ]

  messageFilter: MessageFilterValue = this.messageFilters[0].value


  noticeProps: NoticeProps = {
    isShow: false,
    duration: 0,
    deleteButton: true,
    type: {'is-info': true}
  }

  noticePropsHasPendingMessage: NoticeProps = {
    ...this.noticeProps,
    type: {'is-danger': true},
    message: 'messenger.hasPendingMessage',
  }

  noticePropsHasUnreadMessage: NoticeProps = {
    ...this.noticeProps,
    message: 'messenger.hasUnreadMessage',
  }

  get theReferenceObjectIds(){
    return this.referenceObjectIds || []
  }

  @Watch('messageFilters')
  changeMessageStatus(filters: Array<MessageFilter>){
    filters.forEach((f) => {
      if(f.value === 'pending'){
        this.noticePropsHasPendingMessage.isShow = !! f.count
      }
      if(f.value === 'unread'){
        this.noticePropsHasUnreadMessage.isShow = !! f.count
      }
      if(f.count == 0 && f.value === this.messageFilter){
        this.messageFilter = this.messageFilters[0].value
      }
    })
  }

  isUnreadMessage(message: MessageBox){
    if(! message.receiverUserStatus){
      return false
    }
    if(! message.receiverUserStatus[this.userId]){
      return false
    }
    return message.receiverUserStatus[this.userId].status === 'unread'
  }

  @Watch('messageBox')
  updateMessageBox(messageBox: MessageBox[]){
    this.messageFilters = this.messageFilters.map((mS) => {
      const temp = mS
      if(temp.value === 'all'){
        temp.count = messageBox.length
        return temp
      }

      if(temp.value === 'unread'){
        temp.count = this.messageBox.filter((message) => {return this.isUnreadMessage(message)}).length
        return temp
      }

      temp.count = messageBox.filter((message) => {return message.status === temp.value}).length
      
      return temp

    })
  }

  schedulingObjectProps = {
    title: '',
    schedulingSheetId: '',
    entryUser: '',
    isShow: false,
  }

  get userId(){
    return this.$store.getters.userId as string
  }

  getStatus(messageId: string){
    const theMessage = this.sentReceivedMessages.find((message) => {
      return message.docId === messageId
    })
    if(theMessage && theMessage.status && theMessage.receiverUserIds && theMessage.receiverUserIds[0]){
      return {
        status: theMessage.status,
        userId: theMessage.receiverUserIds[0],
      }
    }
    return undefined
  }

  get messageBox(){
    let messages:  MessageBox[] = []
    //[pending sent draft]
    messages = messages.concat(this.sentMessages.filter(() => {
      return true
    }).map((message) => {
      if(message.status === 'sent' && message.docId){
        const messageBox: MessageBox = {
          ...message,
          receiverUserStatus: {},
        }
        
        if(messageBox.receivedMessageIds){
          messageBox.receivedMessageIds.forEach((receivedMessageId) => {
            const status = this.getStatus(receivedMessageId)
            if(status && messageBox.receiverUserStatus){
              messageBox.receiverUserStatus[status.userId] = {
                status: status.status,
                receivedMessageId: receivedMessageId
              }
            }
          })
        }
        return messageBox
      }
      return message
    }))

    messages = messages.concat(this.receivedMessages.filter((message) => {
      return message.senderUserId != this.userId
    }).map((message) => {
      return {...message}
    }))

    messages = messages.map((message) => {
      if(this.messageFilter === 'all' || this.messageFilter === message.status || (this.messageFilter === 'unread' && this.isUnreadMessage(message))){
        return {
          ...message,
          isShow: true
        }
      }
      else{
        return {
          ...message,
          isShow: false
        }
      }
    })

    messages.sort((b,a) => {
      if(! a.creationTime || ! b.creationTime){
        return 0
      }
      return a.creationTime?.toMillis() - b.creationTime?.toMillis()
    })

    return messages
  }

  startDate: Date = new Date()

  @Watch('startDate')
  async changeStartDate(date: Date){
    if(date){
      const count = await this.getCount(date)

      if(count > 0){
        this.showReadMore = true
        const endDate = DateTime.fromJSDate(date).minus($retrievalPeriod).toJSDate()
        const nextTermCount = await this.getCount(endDate)
        if(nextTermCount === count){
          //next term is empty.
          this.startDate = endDate
        }
      }else{
        this.showReadMore = false
      }
    }
  }

  showReadMore = false

  confirmUpdateMessageStatus = {
    isLoading: false,
    title: '',
    message: '',
    args: {messageId: '',status: ''},
    isShow: false
  }

  showModalUpdateMessageStatus(messageId: string,status: MessageBox['status']){
    if(messageId && status){
      const message = ['unread','read'].includes(status) ? this.receivedMessages.find((message) => {return message.docId === messageId}) : this.sentMessages.find((message) => {return message.docId === messageId})
      if(message){
        const title = (status == 'approved' ? 'message.approve' : '')
        //const modalMessage = (status == 'approved' ? 'messenger.approveDescription' : '')
        this.confirmUpdateMessageStatus = {
          ...this.confirmUpdateMessageStatus,
          title: title,
          message: message.body,
          args: {
            messageId: messageId,
            status: status
          },
          isShow: true
        }
      }
    }
  }

  async updateMessageStatus(event: false | {messageId: string; status: MessageBox['status']}){
    console.log(event)
    try{
      if(event && event.messageId && event.status){
        this.isLoading = event.messageId

        this.confirmUpdateMessageStatus = {
          ...this.confirmUpdateMessageStatus,
          isLoading: true
        }

        if(['unread','read','like'].includes(event.status)){
          const messageDocRef = this.$getUserReceivedMessageDocRef(event.messageId)
          const messageDoc = await getDoc(messageDocRef)
          const message = messageDoc.data()
          if(event.status === 'read' && message && ['unread','like'].includes(message.status)){
            const data = {
              status: event.status,
            }
            await this.$updateDoc(messageDocRef,data)
          }else if(event.status === 'unread' && message && message.status === 'read'){
            const data = {
              status: event.status,
            }
            await this.$updateDoc(messageDocRef,data)
          }else if(event.status === 'like' && message && message.status !== 'like'){
            const data = {
              status: event.status,
            }
            await this.$updateDoc(messageDocRef,data)
          }
        }else if(['approved'].includes(event.status)){
          const messageDocRef = this.$getUserSentMessageDocRef(event.messageId)
          const messageDoc = await getDoc(messageDocRef)
          const message = messageDoc.data()
          if(message && message.status === 'pending'){
            const data = {
              status: event.status,
            }
            await this.$updateDoc(messageDocRef,data)
          }
        }
      }
    }catch(error){
      console.log(error)
    }finally{
      this.confirmUpdateMessageStatus = {
        ...this.confirmUpdateMessageStatus,
        isLoading: false,
        isShow: false
      }
      this.isLoading = false
    }
  }

  async created(){
    try{
      this.$store.commit("setLoading",true);
      this.isLoading = true

      this.getMessages(this.startDate,true)
    }catch(error){
      console.log(error)
    }
  }

  async getCount(startDate: Date){
    let count = 0

    if(this.theReferenceObjectIds.length == 0){
      return count
    }

    const queryConstraints = [orderBy('creationTime','desc'),startAt(startDate)]
    const receivedMessageQuery = query(this.$getUserReceivedMessageQuery(),...queryConstraints)
    
    const sentMessageQuery = query(this.$getReferenceObjectUserSentMessageQuery(this.theReferenceObjectIds),where('senderUserId', '==' , this.userId),...queryConstraints)



    const [receivedMessageCount,sentMessageCount] = await Promise.all([
      getCountFromServer(receivedMessageQuery),
      getCountFromServer(sentMessageQuery)
    ])

    count += receivedMessageCount.data().count
    count += sentMessageCount.data().count
    return count
  }

  getMessages(startDate: Date,init = false){
    if(this.theReferenceObjectIds.length == 0){
      return
    }

    const endDate = DateTime.fromJSDate(startDate).minus($retrievalPeriod).toJSDate()

    const queryConstraints: QueryConstraint[] = [orderBy('creationTime','desc'),endBefore(endDate)]

    if(! init){
      queryConstraints.push(startAt(startDate))
    }

    const receivedMessageQuery = query(this.$getUserReceivedMessageQuery(),...queryConstraints)
    
    const sentReceivedMessageQuery = query(this.$getReferenceObjectUserReceivedMessageQuery(this.theReferenceObjectIds),where('senderUserId', '==' , this.userId),...queryConstraints)
    
    const sentMessageQuery = query(this.$getReferenceObjectUserSentMessageQuery(this.theReferenceObjectIds),where('senderUserId', '==' , this.userId),...queryConstraints)

    this.observers.push( onSnapshot(receivedMessageQuery,(snapshot) => {
      snapshot.docs.filter((doc) => {
        const referenceObjectIds: string[] = doc.get('referenceObjectIds')
        return this.theReferenceObjectIds.some((objectId) => {return referenceObjectIds.includes(objectId)})
      }).forEach((doc) => {
        const row = doc.data() as UserReceivedMessage
        if(! this.messageProps[doc.id]){
          this.messageProps[doc.id] = {
            showBody: false
          }
        }
        const findIndex = this.receivedMessages.findIndex((message) => {return message.docId === doc.id})
        const deleteCount = (findIndex === -1 ? 0 : 1)
        this.receivedMessages.splice(findIndex,deleteCount,row)
      })
    }))

    this.observers.push( onSnapshot(sentReceivedMessageQuery,async (snapshot) => {
      snapshot.docs.forEach((doc) => {
        const row = doc.data() as UserReceivedMessage
        if(! this.messageProps[doc.id]){
          this.messageProps[doc.id] = {
            showBody: false
          }
        }
        const findIndex = this.sentReceivedMessages.findIndex((message) => {return message.docId === doc.id})
        const deleteCount = (findIndex === -1 ? 0 : 1)
        this.sentReceivedMessages.splice(findIndex,deleteCount,row)
      })

      this.$store.commit("setLoading",true);
      this.isLoading = true
      await this.$sleep(100)
      this.$store.commit("setLoading",false);
      this.isLoading = false
    }))

    this.observers.push( onSnapshot(sentMessageQuery,async (snapshot) => {
      snapshot.docs.forEach((doc) => {
        const row = doc.data() as UserSentMessage
        if(! this.messageProps[doc.id]){
          this.messageProps[doc.id] = {
            showBody: false
          }
        }
        const findIndex = this.sentMessages.findIndex((message) => {return message.docId === doc.id})
        const deleteCount = (findIndex === -1 ? 0 : 1)
        this.sentMessages.splice(findIndex,deleteCount,row)
      })
      this.$store.commit("setLoading",true);
      this.isLoading = true
      await this.$sleep(100)
      this.$store.commit("setLoading",false);
      this.isLoading = false
    }))

    this.startDate = endDate
  }

  replyAllMessage(message: UserReceivedMessage){
    if(message && message.docId){
      console.log('replayall')
      console.log(message.docId)
      this.$emit("onReplyAllMessage", message.docId)
    }
  }

  editMessage(message: UserSentMessage){
    if(message && message.docId){
      this.$emit("onEditMessage", message.docId)
    }
  }

  replyMessage(message: UserReceivedMessage){
    if(message && message.docId){
      this.$emit("onReplyMessage", message.docId)
    }
  }

  async showObject(objectId: string,type: 'event' | 'scheduling',message: MessageBox){
    if(! objectId){
      return
    }

    const location = {
      name: '',
      query: {}
    }

    const userId = (message && message.receiverUserIds && message.receiverUserIds.length > 0) ? message.receiverUserIds[0] : this.userId


    switch(type){
      case 'event':
        location.name = 'schedule'
        location.query = {eventId: objectId}
        await this.$router.push(location)
        break
      case 'scheduling':
        this.schedulingObjectProps = {
            ...this.schedulingObjectProps,
            schedulingSheetId: objectId,
            entryUser: userId,
            isShow: true
          }
        break
    }
  }

  toggleBody(messageId: string){
    const f = this.messageProps[messageId].showBody ? false : true

    this.messageProps = {...this.messageProps, [messageId]: {showBody : f}}
  }

  destroyed() {
    this.$unsubscriber(this.observers)
  }
}
